import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from '../../configApi/axiosConfig';
import { APIURL } from '../../configApi/apiConfig';
import XLSX from 'xlsx-js-style';
import {
  BarChart2, ChevronRight, ChevronDown, ChevronLeft, ChevronUp,
  RefreshCw, Search, TrendingUp, Users,
  DollarSign, Target, Calendar, Layers, FileDown,
  Percent, Store, X, ChevronDown as ChevronDownSm,
  Briefcase, User, UserCheck, ArrowUpDown,
} from 'lucide-react';
import { ClientesAprobadosModal } from './ClientesAprobadosModal';
import { exportarClientesAprobadosExcel } from './utils/exportarClientesAprobadosExcel';
import { useAuth } from '../AuthContext/AuthContext';
import useBodegaUsuario from '../../hooks/useBodegaUsuario';
import { agruparVendedores, obtenerFilasVisibles } from './agruparVendedores';

// Columnas fijas conocidas del SP — todo lo demás son subgrupos dinámicos
const KNOWN_COLUMNS = new Set([
  'idSupervisor', 'Supervisor', 'idBodega', 'Bodega',
  'idPersonal', 'idNomina', 'Periodo', 'NombreCompleto',
  'EdadAnios', 'EdadMeses', 'TiempoEmpresaAnios', 'TiempoEmpresaMeses',
  'Sueldo', 'Comision', 'Presupuesto', 'Venta', 'PorcentajeCumplimiento',
  'Otros', 'CREDIPOINT', 'TARJETA', 'CONTADO',
  'CedulasIngresadas', 'facturados', 'noAplica', 'preAprobado',
  'DiasLocal', 'DiasPuerteo', 'DiasLibre',
]);

// Rol del reporte: cada uno pega a un SP distinto (misma forma de columnas),
// pero solo "vendedor" tiene detalle de créditos aprobados/pre-aprobados
// (el SP de detalle es por idVendedor, no existe equivalente por supervisor
// ni por jefe de local).
const ROL_META = {
  vendedor: {
    label: 'Vendedor',
    icon: User,
    title: 'Cumplimiento de Ventas por Vendedor',
    desc: 'Análisis de rendimiento, categorías de producto y gestión de crédito por período y vendedor',
    columnaPersona: 'Vendedor',
    personaPlural: 'vendedores',
  },
  supervisor: {
    label: 'Supervisor',
    icon: UserCheck,
    title: 'Cumplimiento de Ventas por Supervisor',
    desc: 'Análisis de rendimiento y categorías de producto agregado por las bodegas a cargo del supervisor',
    columnaPersona: 'Supervisor',
    personaPlural: 'supervisores',
  },
  jefeLocal: {
    label: 'Jefe de Local',
    icon: Briefcase,
    title: 'Cumplimiento de Ventas por Jefe de Local',
    desc: 'Análisis de rendimiento y categorías de producto por jefe de local',
    columnaPersona: 'Jefe de Local',
    personaPlural: 'jefes de local',
  },
};

function getFirstDayOfCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function getToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function fmtCurrency(value) {
  if (value === null || value === undefined) return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return new Intl.NumberFormat('es-EC', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(value) {
  if (value === null || value === undefined) return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return `${n.toFixed(1)}%`;
}

function getCumplColor(pct) {
  const v = parseFloat(pct);
  if (v >= 100) return { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500', ring: 'ring-emerald-200' };
  if (v >= 80)  return { bg: 'bg-amber-100',   text: 'text-amber-700',   bar: 'bg-amber-400',   ring: 'ring-amber-200'   };
  if (v >= 60)  return { bg: 'bg-orange-100',  text: 'text-orange-700',  bar: 'bg-orange-400',  ring: 'ring-orange-200'  };
  return          { bg: 'bg-red-100',    text: 'text-red-700',    bar: 'bg-red-500',    ring: 'ring-red-200'    };
}

function EmptyCell() {
  return <span className="text-slate-300 select-none">—</span>;
}

export function CumplimientoVentasVendedorComponent() {
  const { userData, idMenu } = useAuth();
  const { vendedor: vendedoresBodega, listaVendedoresporBodega } = useBodegaUsuario();

  // Permisos por usuario/menú 
  const [permisos, setPermisos] = useState([]);
  const tienePermiso = (nombrePermiso) => {
    const permiso = permisos.find((p) => p.Permisos === nombrePermiso);
    return !!(permiso && permiso.Activo);
  };
  const puedeExportarBodega = () => tienePermiso('CUMPLIMIENTO VENTAS EXPORTAR BODEGA');
  const puedeExportarTodo = () => tienePermiso('CUMPLIMIENTO VENTAS EXPORTAR TODO');

  const [desdeInput, setDesdeInput] = useState(getFirstDayOfCurrentMonth);
  const [hastaInput, setHastaInput] = useState(getToday);
  const [mostrarPorcentaje, setMostrarPorcentaje] = useState(false);
  const [rol, setRol] = useState('vendedor');
  const rolMeta = ROL_META[rol];
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [cargado, setCargado] = useState(false);

  // Columnas colapsables (subgrupos dinámicos + Otros)
  const [catExpanded, setCatExpanded] = useState(false);

  // Grupos de columnas colapsables — igual que Categorías, pero estos
  // arrancan expandidos. Colapsarlos deja más espacio para Categorías
  // (que queda al final de la tabla) sin tener que hacer scroll horizontal.
  const [rendExpanded, setRendExpanded] = useState(true);
  const [pagoExpanded, setPagoExpanded] = useState(true);
  const [creditoExpanded, setCreditoExpanded] = useState(true);
  const [horarioExpanded, setHorarioExpanded] = useState(true);

  // Cada sección de período se puede colapsar/expandir por separado
  const [periodosColapsados, setPeriodosColapsados] = useState({});
  const periodoColapsado = (periodo) => !!periodosColapsados[periodo];
  const togglePeriodo = (periodo) => setPeriodosColapsados(prev => ({ ...prev, [periodo]: !prev[periodo] }));
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const toggleGrupo = (clave) => setGruposExpandidos(prev => ({ ...prev, [clave]: !prev[clave] }));

  // Ordenamiento de la tabla — aplica igual en los 3 roles (vendedor,
  // supervisor, jefe de local) porque comparten la misma tabla/columnas.
  // ordenColumna es el nombre exacto de la columna en los datos del SP
  // (p.ej. "PorcentajeCumplimiento", un subgrupo dinámico, u "Otros").
  const [ordenColumna, setOrdenColumna] = useState(null);
  const [ordenDireccion, setOrdenDireccion] = useState('desc');

  const toggleOrden = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion('desc');
    }
  };

  const IconoOrden = ({ columna }) => {
    if (ordenColumna !== columna) return <ArrowUpDown size={10} className="opacity-30" />;
    return ordenDireccion === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />;
  };

  // Filtro rápido por nombre de vendedor (client-side)
  const [filtroVendedor, setFiltroVendedor] = useState('');

  // Gestor de oportunidades y seguimiento comercial de un vendedor
  const [modalVendedor, setModalVendedor] = useState(null); // { idPersonal, NombreCompleto }

  // Exportación de clientes aprobados/pre-aprobados por bodega o de todas
  const [exportandoBodega, setExportandoBodega] = useState(false);
  const [exportandoTodo, setExportandoTodo]     = useState(false);

  // Bodega
  const [bodegas, setBodegas]             = useState([]);
  const [selectedBodega, setSelectedBodega] = useState(null);
  const [bodegaQuery, setBodegaQuery]     = useState('');
  const [bodegaOpen, setBodegaOpen]       = useState(false);
  const bodegaRef = useRef(null);

  // Vendedor propio (solo se resuelve/usa cuando el usuario autenticado es
  // un VENDEDOR — idGrupo 23 — igual que en ListadoSolicitud.js: la bodega y
  // el vendedor se auto-seleccionan a partir del usuario logueado y no se
  // pueden cambiar desde la UI)
  const esVendedor = userData?.idGrupo === 23;
  const mostrarJerarquia = rol === 'vendedor' && !esVendedor && data.some(row => row.idBodega != null || row.Bodega);
  // Supervisores y jefes de local comienzan por sus bodegas; los perfiles
  // con alcance más amplio comienzan por el supervisor devuelto por el SP.
  const mostrarSupervisores = mostrarJerarquia && ![14, 21, 22, 24].includes(userData?.idGrupo);
  const [selectedVendedor, setSelectedVendedor] = useState(null);

  // Paginación
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [pageMap, setPageMap] = useState({});

  const getPage   = (periodo) => pageMap[periodo] || 1;
  const setPage   = (periodo, p) => setPageMap(prev => ({ ...prev, [periodo]: p }));
  const resetPages = () => setPageMap({});

  // Subgrupos dinámicos: todas las claves del resultado que NO son conocidas
  const dynamicCols = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]).filter(k => !KNOWN_COLUMNS.has(k));
  }, [data]);

  // Columnas del grupo colapsable
  const catCols = useMemo(() => [...dynamicCols, 'Otros'], [dynamicCols]);

  // Datos filtrados por nombre
  const dataFiltrada = useMemo(() => {
    const q = filtroVendedor.trim().toLowerCase();
    if (!q) return data;
    return data.filter(r => r.NombreCompleto?.toLowerCase().includes(q));
  }, [data, filtroVendedor]);

  // Períodos únicos (del subconjunto filtrado)
  const periodos = useMemo(() => {
    const seen = new Set();
    return dataFiltrada.reduce((acc, row) => {
      if (!seen.has(row.Periodo)) { seen.add(row.Periodo); acc.push(row.Periodo); }
      return acc;
    }, []);
  }, [dataFiltrada]);

  const fetchData = async () => {
    if (!desdeInput || !hastaInput) return;

    if (desdeInput > hastaInput) {
      setError('La fecha "desde" no puede ser mayor a la fecha "hasta".');
      return;
    }
    if (hastaInput > getToday()) {
      setError('La fecha "hasta" no puede ser mayor a la fecha actual.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {
        desde: desdeInput,
        hasta: hastaInput,
        idBodega: selectedBodega?.b_Bodega ?? null,
        mostrarPorcentaje: mostrarPorcentaje ? 1 : 0,
      };
      const url = rol === 'supervisor'
        ? APIURL.getCumplimientoVentasSupervisor(params)
        : rol === 'jefeLocal'
          ? APIURL.getCumplimientoVentasJefeLocal(params)
          : APIURL.getCumplimientoVentasVendedor(params);

      const res = await axios.get(url);
      setData(Array.isArray(res.data) ? res.data : []);
      setCargado(true);
    } catch {
      setError('Error al ejecutar el reporte. Verifica los filtros e intenta de nuevo.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar lista de bodegas (el backend ya devuelve solo las bodegas asignadas al usuario)
  useEffect(() => {
    axios.get(APIURL.usuario_bodega_bodega_user())
      .then(res => setBodegas(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const permissionscomponents = async (idMenuItems, idUsuario) => {
    try {
      const res = await axios.get(APIURL.getacces(idMenuItems, idUsuario));
      setPermisos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar permisos:', err);
    }
  };

  useEffect(() => {
    if (userData?.idUsuario && idMenu) {
      permissionscomponents(idMenu, userData.idUsuario);
    }
  }, [idMenu, userData?.idUsuario]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si el usuario autenticado es VENDEDOR, se auto-selecciona su única
  // bodega (misma que ListadoSolicitud.js) y se piden los vendedores de esa
  // bodega para resolver su propio idPersonal por Codigo === userData.Nombre
  useEffect(() => {
    if (esVendedor && bodegas.length > 0 && !selectedBodega) {
      const bodegaPropia = bodegas[0];
      setSelectedBodega(bodegaPropia);
      const hoy = new Date().toISOString().split('T')[0];
      listaVendedoresporBodega(hoy, bodegaPropia.b_Bodega, 0);
    }
  }, [esVendedor, bodegas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!esVendedor || !Array.isArray(vendedoresBodega)) return;
    const propio = vendedoresBodega.find(v => v.Codigo === userData.Nombre);
    if (propio) setSelectedVendedor(propio.idPersonal);
  }, [esVendedor, userData?.Nombre, vendedoresBodega]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carga inicial del reporte: para un VENDEDOR se espera a tener resuelto
  // su propio idVendedor (evita mostrar de entrada el reporte de todos)
  useEffect(() => {
    if (!userData) return;
    if (esVendedor) {
      if (selectedVendedor) fetchData();
    } else {
      fetchData();
    }
  }, [userData, esVendedor, selectedVendedor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cerrar dropdown de bodega al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (bodegaRef.current && !bodegaRef.current.contains(e.target)) {
        setBodegaOpen(false);
        setBodegaQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Re-fetch cuando cambia el toggle de porcentaje (si ya había datos)
  useEffect(() => {
    if (cargado) fetchData();
  }, [mostrarPorcentaje]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch cuando cambia el rol del reporte (vendedor/supervisor/jefe local)
  useEffect(() => {
    if (cargado) fetchData();
  }, [rol]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resetear páginas al cambiar filtro, datos o filas por página
  useEffect(() => { resetPages(); }, [filtroVendedor, data, rowsPerPage, ordenColumna, ordenDireccion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resumen por período
  const getPeriodSummary = (periodo) => {
    let rows = dataFiltrada.filter(r => r.Periodo === periodo);
    if (ordenColumna) {
      rows = [...rows].sort((a, b) => {
        const av = parseFloat(a[ordenColumna]) || 0;
        const bv = parseFloat(b[ordenColumna]) || 0;
        return ordenDireccion === 'asc' ? av - bv : bv - av;
      });
    }
    const totalVenta       = rows.reduce((s, r) => s + parseFloat(r.Venta       || 0), 0);
    const totalPresupuesto = rows.reduce((s, r) => s + parseFloat(r.Presupuesto || 0), 0);
    const avgCumpl         = totalPresupuesto > 0 ? (totalVenta / totalPresupuesto) * 100 : 0;
    // Forma de pago — siempre es absoluto (el SP retorna valor, no %)
    const totalCredi   = rows.reduce((s, r) => s + parseFloat(r.CREDIPOINT || 0), 0);
    const totalTarjeta = rows.reduce((s, r) => s + parseFloat(r.TARJETA    || 0), 0);
    const totalContado = rows.reduce((s, r) => s + parseFloat(r.CONTADO    || 0), 0);
    // Crédito — siempre conteos
    const totalCedulas    = rows.reduce((s, r) => s + parseInt(r.CedulasIngresadas || 0), 0);
    const totalFacturados = rows.reduce((s, r) => s + parseInt(r.facturados         || 0), 0);
    const totalNoAplica   = rows.reduce((s, r) => s + parseInt(r.noAplica           || 0), 0);
    const totalPreAprob   = rows.reduce((s, r) => s + parseInt(r.preAprobado        || 0), 0);
    return { rows, totalVenta, totalPresupuesto, avgCumpl, totalCredi, totalTarjeta, totalContado, totalCedulas, totalFacturados, totalNoAplica, totalPreAprob };
  };

  // Render de celda dinámica (subgrupo o forma de pago) según modo
  const renderDynCell = (value) => {
    const n = parseFloat(value);
    if (isNaN(n) || n === 0) return <EmptyCell />;
    const formatted = mostrarPorcentaje ? fmtPct(n) : fmtCurrency(n);
    return formatted ?? <EmptyCell />;
  };

  // Número de columnas ocupadas por el bloque de categorías
  const catColspan = catExpanded ? catCols.length : 1;
  const rendColspan = rendExpanded ? 3 : 1;
  const pagoColspan = pagoExpanded ? 3 : 1;
  const creditoColspan = creditoExpanded ? 4 : 1;
  const horarioColspan = horarioExpanded ? 3 : 1;

  const exportToExcel = () => {
    if (!dataFiltrada.length) return;

    const HEADER_STYLE = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '2d3689' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
        right:  { style: 'thin', color: { rgb: 'FFFFFF' } },
      },
    };
    const TOTAL_STYLE = {
      font: { bold: true, sz: 10, color: { rgb: '2d3689' } },
      fill: { fgColor: { rgb: 'E8EAF6' } },
      alignment: { horizontal: 'right' },
    };
    const TOTAL_LABEL_STYLE = { ...TOTAL_STYLE, alignment: { horizontal: 'left' } };

    const headers = [
      'Período', rolMeta.columnaPersona, 'Edad (años)', 'Tiempo Empresa',
      'Presupuesto', 'Venta', '% Cumplimiento',
      mostrarPorcentaje ? 'CrediPoint (%)' : 'CrediPoint',
      mostrarPorcentaje ? 'Tarjeta (%)' : 'Tarjeta',
      mostrarPorcentaje ? 'Contado (%)' : 'Contado',
      'Cédulas Ingresadas', 'Facturados', 'No Aplica', 'Pre-Aprobados',
      'Días Local', 'Días Puerteo', 'Días Libre',
      ...dynamicCols.map(c => mostrarPorcentaje ? `${c} (%)` : c),
      mostrarPorcentaje ? 'Otros (%)' : 'Otros',
      ...(rol === 'vendedor' ? ['Supervisor', 'Bodega'] : []),
    ];

    const allRows = [];

    periodos.forEach(periodo => {
      const rows = dataFiltrada.filter(r => r.Periodo === periodo);
      const totalVenta       = rows.reduce((s, r) => s + parseFloat(r.Venta       || 0), 0);
      const totalPresupuesto = rows.reduce((s, r) => s + parseFloat(r.Presupuesto || 0), 0);
      const avgCumpl         = totalPresupuesto > 0 ? (totalVenta / totalPresupuesto) * 100 : 0;
      const totalCredi   = rows.reduce((s, r) => s + parseFloat(r.CREDIPOINT || 0), 0);
      const totalTarjeta = rows.reduce((s, r) => s + parseFloat(r.TARJETA    || 0), 0);
      const totalContado = rows.reduce((s, r) => s + parseFloat(r.CONTADO    || 0), 0);
      const totalCedulas    = rows.reduce((s, r) => s + parseInt(r.CedulasIngresadas || 0), 0);
      const totalFacturados = rows.reduce((s, r) => s + parseInt(r.facturados         || 0), 0);
      const totalNoAplica   = rows.reduce((s, r) => s + parseInt(r.noAplica           || 0), 0);
      const totalPreAprob   = rows.reduce((s, r) => s + parseInt(r.preAprobado        || 0), 0);

      rows.forEach(row => {
        allRows.push({
          values: [
            row.Periodo,
            row.NombreCompleto,
            parseFloat(row.EdadAnios) || 0,
            `${row.TiempoEmpresaAnios}a ${row.TiempoEmpresaMeses}m`,
            parseFloat(row.Presupuesto) || 0,
            parseFloat(row.Venta) || 0,
            parseFloat(row.PorcentajeCumplimiento) || 0,
            parseFloat(row.CREDIPOINT) || 0,
            parseFloat(row.TARJETA) || 0,
            parseFloat(row.CONTADO) || 0,
            parseInt(row.CedulasIngresadas) || 0,
            parseInt(row.facturados) || 0,
            parseInt(row.noAplica) || 0,
            parseInt(row.preAprobado) || 0,
            parseInt(row.DiasLocal) || 0,
            parseInt(row.DiasPuerteo) || 0,
            parseInt(row.DiasLibre) || 0,
            ...dynamicCols.map(col => parseFloat(row[col]) || 0),
            parseFloat(row.Otros) || 0,
            ...(rol === 'vendedor' ? [row.Supervisor || '', row.Bodega || ''] : []),
          ],
          isTotal: false,
        });
      });

      // Fila de totales del período
      allRows.push({
        values: [
          `TOTAL ${periodo}`, '',
          '', '',
          totalPresupuesto, totalVenta, avgCumpl,
          mostrarPorcentaje ? '' : totalCredi,
          mostrarPorcentaje ? '' : totalTarjeta,
          mostrarPorcentaje ? '' : totalContado,
          totalCedulas, totalFacturados, totalNoAplica, totalPreAprob,
          '', '', '',
          ...dynamicCols.map(() => ''),
          '',
          ...(rol === 'vendedor' ? ['', ''] : []),
        ],
        isTotal: true,
      });
    });

    const wsData = [headers, ...allRows.map(r => r.values)];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Estilos de cabecera
    headers.forEach((_, c) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[ref]) ws[ref].s = HEADER_STYLE;
    });

    // Estilos de filas de datos y totales
    allRows.forEach((row, i) => {
      const r = i + 1;
      row.values.forEach((_, c) => {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) ws[ref] = { v: '' };
        if (row.isTotal) {
          ws[ref].s = c === 0 ? TOTAL_LABEL_STYLE : TOTAL_STYLE;
        } else {
          const even = i % 2 === 0;
          ws[ref].s = {
            font: { sz: 9 },
            fill: { fgColor: { rgb: even ? 'FFFFFF' : 'F8F9FF' } },
            alignment: { horizontal: c <= 1 || (rol === 'vendedor' && c >= headers.length - 2) ? 'left' : 'right' },
          };
        }
      });
    });

    // Formatos numéricos
    const COL_CURRENCY = [4, 5, 7, 8, 9, ...dynamicCols.map((_, i) => 17 + i), 17 + dynamicCols.length];
    const COL_PCT      = [6];
    allRows.forEach((row, i) => {
      const r = i + 1;
      if (!row.isTotal || mostrarPorcentaje) {
        COL_CURRENCY.forEach(c => {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = '"$"#,##0';
        });
      }
      COL_PCT.forEach(c => {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = '0.00"%"';
      });
    });

    // Anchos de columna
    ws['!cols'] = headers.map((_, i) => {
      if (i === 1) return { wch: 32 };
      if (i === 0) return { wch: 14 };
      if (i === 3) return { wch: 14 };
      if (rol === 'vendedor' && i >= headers.length - 2) return { wch: 30 };
      return { wch: 14 };
    });

    // Fila de cabecera con altura mayor
    ws['!rows'] = [{ hpt: 28 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cumplimiento Ventas');
    XLSX.writeFile(wb, `CumplimientoVentas_${desdeInput}_${hastaInput}.xlsx`);
  };

  // Trae y exporta los clientes aprobados/pre-aprobados de todos los
  // vendedores de la bodega seleccionada (idVendedor null -> el SP no filtra
  // por vendedor, solo por bodega).
  const exportarClientesAprobadosPorBodega = async () => {
    if (!selectedBodega) return;
    setExportandoBodega(true);
    try {
      const res = await axios.get(
        APIURL.getCumplimientoVentasVendedorClientesAprobados(null, selectedBodega.b_Bodega)
      );
      exportarClientesAprobadosExcel(res.data, `ClientesAprobados_${selectedBodega.b_Nombre}`);
    } catch (err) {
      console.error('Error al exportar clientes aprobados por bodega:', err);
    } finally {
      setExportandoBodega(false);
    }
  };

  // Trae y exporta los clientes aprobados/pre-aprobados de TODAS las bodegas
  // que este usuario tiene asignadas. El backend resuelve esas bodegas del
  // usuario autenticado y llama al SP una sola vez con @idBodegas (lista CSV)
  // — antes esto era 1 petición HTTP por bodega desde el frontend (N
  // round-trips + N EXEC separados en SQL Server), lo que además era la causa
  // real del out-of-memory al exportar "todos los créditos". El Excel se
  // sigue generando sin estilos por celda (estilos: false) porque el dataset
  // combinado igual puede ser grande.
  const exportarClientesAprobadosTodo = async () => {
    setExportandoTodo(true);
    try {
      const res = await axios.get(APIURL.getCumplimientoVentasVendedorClientesAprobadosTodas());
      const filas = Array.isArray(res.data) ? res.data : [];
      exportarClientesAprobadosExcel(filas, `ClientesAprobados_Todos_${desdeInput}_${hastaInput}`, { estilos: false });
    } catch (err) {
      console.error('Error al exportar todos los clientes aprobados:', err);
    } finally {
      setExportandoTodo(false);
    }
  };

  return (
    <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
      <div className="mx-auto max-w-[1800px] space-y-4">

        {/* ── Header ────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-morado/10 text-morado">
            <BarChart2 size={24} />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold tracking-tight text-slate-800">{rolMeta.title}</h1>
            <p className="mt-0.5 text-xs text-slate-500">{rolMeta.desc}</p>
          </div>
        </div>

        {/* ── Filtros ───────────────────────────────────────────── */}
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">

            {/* Rango de fechas — agrupado */}
            <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <input
                type="date"
                value={desdeInput}
                max={hastaInput || getToday()}
                onChange={e => setDesdeInput(e.target.value)}
                title="Desde"
                className="w-[118px] bg-transparent text-xs text-slate-700 focus:outline-none"
              />
              <span className="select-none text-slate-300 px-0.5">—</span>
              <input
                type="date"
                value={hastaInput}
                min={desdeInput || undefined}
                max={getToday()}
                onChange={e => setHastaInput(e.target.value)}
                title="Hasta"
                className="w-[118px] bg-transparent text-xs text-slate-700 focus:outline-none"
              />
            </div>

            <div className="h-5 w-px bg-slate-200" />

            {/* Bodega — auto-seleccionada y bloqueada para un VENDEDOR (idGrupo 23) */}
            <div className="relative" ref={bodegaRef}>
              <div
                title={esVendedor ? 'Tu bodega asignada' : undefined}
                className={`flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-1.5 ${
                  esVendedor ? 'bg-slate-100' : 'bg-slate-50 focus-within:border-morado focus-within:ring-2 focus-within:ring-morado/20'
                }`}
              >
                <Store size={13} className="flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={bodegaQuery}
                  onChange={e => { if (!esVendedor) { setBodegaQuery(e.target.value); setBodegaOpen(true); } }}
                  onFocus={() => { if (!esVendedor) setBodegaOpen(true); }}
                  disabled={esVendedor}
                  placeholder={selectedBodega ? selectedBodega.b_Nombre : 'Bodega'}
                  className="w-36 bg-transparent text-xs text-slate-700 placeholder-slate-400 focus:outline-none disabled:cursor-not-allowed"
                />
                {esVendedor ? null : selectedBodega ? (
                  <button onClick={() => { setSelectedBodega(null); setBodegaQuery(''); }} className="flex-shrink-0 text-slate-400 hover:text-slate-600">
                    <X size={12} />
                  </button>
                ) : (
                  <ChevronDownSm size={12} className="flex-shrink-0 text-slate-400 pointer-events-none" />
                )}
              </div>
              {bodegaOpen && !esVendedor && (
                <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                  <button
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { setSelectedBodega(null); setBodegaQuery(''); setBodegaOpen(false); }}
                    className={`flex w-full items-center px-3 py-2 text-left text-xs transition-colors hover:bg-slate-50 ${!selectedBodega ? 'font-semibold text-morado' : 'text-slate-500'}`}
                  >
                    Todas las bodegas
                  </button>
                  <div className="border-t border-slate-100" />
                  {bodegas
                    .filter(b => b.b_Nombre?.toLowerCase().includes(bodegaQuery.toLowerCase()))
                    .map(b => (
                      <button
                        key={b.b_Bodega}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setSelectedBodega(b); setBodegaQuery(''); setBodegaOpen(false); }}
                        className={`flex w-full items-center px-3 py-2 text-left text-xs transition-colors hover:bg-slate-50 ${selectedBodega?.b_Bodega === b.b_Bodega ? 'font-semibold text-morado bg-morado/5' : 'text-slate-700'}`}
                      >
                        {b.b_Nombre}
                      </button>
                    ))
                  }
                  {bodegas.filter(b => b.b_Nombre?.toLowerCase().includes(bodegaQuery.toLowerCase())).length === 0 && (
                    <p className="px-3 py-2 text-xs italic text-slate-400">Sin resultados</p>
                  )}
                </div>
              )}
            </div>

            <div className="h-5 w-px bg-slate-200" />

            {/* Rol del reporte — oculto para un VENDEDOR (idGrupo 23), que
                solo puede ver su propio reporte de vendedor */}
            {!esVendedor && (
              <>
                <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                  {Object.entries(ROL_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <button
                        key={key}
                        title={meta.title}
                        onClick={() => setRol(key)}
                        className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all ${
                          rol === key ? 'bg-white text-morado shadow-sm ring-1 ring-slate-200' : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        <Icon size={11} /> {meta.label}
                      </button>
                    );
                  })}
                </div>
                <div className="h-5 w-px bg-slate-200" />
              </>
            )}

            {/* Toggle $ / % — compacto */}
            <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              <button
                title="Ver valores en monto ($)"
                onClick={() => setMostrarPorcentaje(false)}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all ${
                  !mostrarPorcentaje ? 'bg-white text-morado shadow-sm ring-1 ring-slate-200' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <DollarSign size={11} /> Monto
              </button>
              <button
                title="Ver valores como % de participación"
                onClick={() => setMostrarPorcentaje(true)}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all ${
                  mostrarPorcentaje ? 'bg-white text-morado shadow-sm ring-1 ring-slate-200' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <Percent size={11} /> %
              </button>
            </div>

            <div className="h-5 w-px bg-slate-200" />

            {/* Acciones */}
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-xl bg-morado px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-moradoHover disabled:opacity-60"
            >
              {loading ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />}
              {loading ? 'Cargando...' : 'Consultar'}
            </button>

            {dataFiltrada.length > 0 && (
              <button
                onClick={exportToExcel}
                title="Exportar a Excel"
                className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
              >
                <FileDown size={13} /> Excel
              </button>
            )}

            {rol === 'vendedor' && puedeExportarBodega() && (
              <button
                onClick={exportarClientesAprobadosPorBodega}
                disabled={!selectedBodega || exportandoBodega}
                title={selectedBodega ? 'Exportar clientes aprobados/pre-aprobados de esta bodega' : 'Selecciona una bodega primero'}
                className="flex items-center gap-1.5 rounded-xl bg-morado px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-moradoHover disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {exportandoBodega ? <RefreshCw size={13} className="animate-spin" /> : <FileDown size={13} />}
                Creditos Bodega
              </button>
            )}

            {rol === 'vendedor' && puedeExportarTodo() && (
              <button
                onClick={exportarClientesAprobadosTodo}
                disabled={bodegas.length === 0 || exportandoTodo}
                title="Exportar clientes aprobados/pre-aprobados de todas tus bodegas asignadas"
                className="flex items-center gap-1.5 rounded-xl bg-primaryPurple px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {exportandoTodo ? <RefreshCw size={13} className="animate-spin" /> : <FileDown size={13} />}
                {exportandoTodo ? 'Exportando...' : 'Creditos de vendedores.'}
              </button>
            )}

            {/* Buscador vendedor */}
            {data.length > 0 && (
              <div className="relative ml-auto">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filtroVendedor}
                  onChange={e => setFiltroVendedor(e.target.value)}
                  placeholder={`Filtrar ${rolMeta.columnaPersona.toLowerCase()}...`}
                  className="rounded-xl border border-slate-200 bg-slate-50 py-1.5 pl-7 pr-3 text-xs focus:border-morado focus:bg-white focus:outline-none focus:ring-2 focus:ring-morado/20"
                />
              </div>
            )}
          </div>

          {mostrarPorcentaje && (
            <p className="mt-2 text-[11px] text-slate-500 bg-indigo-50 rounded-lg px-3 py-1.5 border border-indigo-100">
              <span className="font-semibold text-indigo-700">Modo % participación:</span> categorías y forma de pago muestran su peso sobre la venta total. Presupuesto y Venta siempre en monto.
            </p>
          )}
        </div>

        {/* ── Error ─────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Loading ───────────────────────────────────────────── */}
        {loading && (
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white py-20 text-slate-500 shadow-sm">
            <RefreshCw size={22} className="animate-spin text-morado" />
            <span className="text-sm font-medium">Ejecutando reporte...</span>
          </div>
        )}

        {/* ── Sin datos ─────────────────────────────────────────── */}
        {!loading && cargado && dataFiltrada.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white py-20 text-center shadow-sm">
            <BarChart2 size={40} className="text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">Sin datos para el período seleccionado</p>
            <p className="text-xs text-slate-400">Ajusta los filtros y vuelve a consultar</p>
          </div>
        )}

        {/* ── Contenido por período ─────────────────────────────── */}
        {!loading && periodos.map(periodo => {
          const {
            rows, totalVenta, totalPresupuesto, avgCumpl,
            totalCredi, totalTarjeta, totalContado,
            totalCedulas, totalFacturados, totalNoAplica, totalPreAprob,
          } = getPeriodSummary(periodo);

          if (rows.length === 0) return null;
          const cc = getCumplColor(avgCumpl);

          // Paginar grupos completos evita partir una bodega entre páginas.
          const grupos = mostrarJerarquia ? agruparVendedores(rows, mostrarSupervisores) : [];
          const totalItems = mostrarJerarquia ? grupos.length : rows.length;
          const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
          const page = Math.min(getPage(periodo), totalPages);
          const pageItems = mostrarJerarquia
            ? grupos.slice((page - 1) * rowsPerPage, page * rowsPerPage)
            : rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
          const pageRows = mostrarJerarquia
            ? obtenerFilasVisibles(pageItems, mostrarSupervisores, gruposExpandidos, periodo)
            : pageItems.map((fila, indice) => ({ tipo: 'vendedor', fila, indice: (page - 1) * rowsPerPage + indice }));
          const firstItem  = (page - 1) * rowsPerPage + 1;
          const lastItem   = Math.min(page * rowsPerPage, totalItems);
          const columnasTabla = 2 + rendColspan + pagoColspan + creditoColspan + horarioColspan + catColspan;
          const tipoGrupo = mostrarSupervisores ? 'supervisores' : 'bodegas';

          return (
            <div key={periodo} className="space-y-2">

              {/* Cabecera de período con KPIs */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 shadow-sm">
                <button
                  onClick={() => togglePeriodo(periodo)}
                  title={periodoColapsado(periodo) ? 'Expandir período' : 'Colapsar período'}
                  className="flex-shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  {periodoColapsado(periodo) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-morado" />
                  <span className="text-sm font-bold text-slate-800">{periodo}</span>
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <div className="flex items-center gap-1.5">
                  <Users size={14} className="text-slate-400" />
                  <span className="text-xs text-slate-600">
                    <span className="font-bold text-slate-800">{rows.length}</span> {rolMeta.personaPlural}
                  </span>
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <div className="flex items-center gap-1.5">
                  <Target size={14} className="text-slate-400" />
                  <span className="text-xs text-slate-600">
                    Presupuesto: <span className="font-bold text-slate-800">{fmtCurrency(totalPresupuesto)}</span>
                  </span>
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <div className="flex items-center gap-1.5">
                  <DollarSign size={14} className="text-slate-400" />
                  <span className="text-xs text-slate-600">
                    Venta: <span className="font-bold text-slate-800">{fmtCurrency(totalVenta)}</span>
                  </span>
                </div>
                <div className={`ml-auto flex items-center gap-1.5 rounded-xl px-3 py-1.5 ring-1 ${cc.bg} ${cc.ring}`}>
                  <TrendingUp size={13} className={cc.text} />
                  <span className={`text-xs font-bold ${cc.text}`}>{fmtPct(avgCumpl)} cumplimiento</span>
                </div>
              </div>

              {!periodoColapsado(periodo) && (
              <>
              {/* Tabla */}
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-xs">
                  <thead>

                    {/* Fila 1: grupos de columnas */}
                    <tr>
                      {/* # y Vendedor — rowSpan=2 */}
                      <th rowSpan={2} className="border-b-2 border-r border-b-slate-200 border-r-slate-100 bg-slate-50 px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400 w-8">
                        #
                      </th>
                      <th rowSpan={2} className="sticky left-0 z-20 border-b-2 border-r border-b-slate-200 border-r-slate-100 bg-slate-50 px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 min-w-[160px] shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]">
                        {rolMeta.columnaPersona}
                      </th>

                      {/* Rendimiento — clickable toggle */}
                      <th
                        colSpan={rendColspan}
                        onClick={() => setRendExpanded(v => !v)}
                        className="cursor-pointer select-none border-b border-r border-b-morado/20 border-r-slate-100 bg-morado/5 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-morado transition-colors hover:bg-morado/10"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          Rendimiento
                          {rendExpanded
                            ? <ChevronDown size={11} />
                            : <><ChevronRight size={11} /><span className="rounded-full bg-morado px-1.5 py-0.5 text-[9px] font-bold text-white">3</span></>
                          }
                        </span>
                      </th>

                      {/* Forma de pago — clickable toggle */}
                      <th
                        colSpan={pagoColspan}
                        onClick={() => setPagoExpanded(v => !v)}
                        className="cursor-pointer select-none border-b border-r border-b-emerald-200 border-r-slate-100 bg-emerald-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          Forma de Pago
                          {pagoExpanded
                            ? <ChevronDown size={11} />
                            : <><ChevronRight size={11} /><span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold text-white">3</span></>
                          }
                        </span>
                      </th>

                      {/* Crédito — clickable toggle */}
                      <th
                        colSpan={creditoColspan}
                        onClick={() => setCreditoExpanded(v => !v)}
                        className="cursor-pointer select-none border-b border-r border-b-amber-200 border-r-slate-100 bg-amber-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-amber-700 transition-colors hover:bg-amber-100"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          Crédito
                          {creditoExpanded
                            ? <ChevronDown size={11} />
                            : <><ChevronRight size={11} /><span className="rounded-full bg-amber-600 px-1.5 py-0.5 text-[9px] font-bold text-white">4</span></>
                          }
                        </span>
                      </th>

                      {/* Horario — clickable toggle */}
                      <th
                        colSpan={horarioColspan}
                        onClick={() => setHorarioExpanded(v => !v)}
                        className="cursor-pointer select-none border-b border-r border-b-sky-200 border-r-slate-100 bg-sky-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-sky-700 transition-colors hover:bg-sky-100"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          Horario
                          {horarioExpanded
                            ? <ChevronDown size={11} />
                            : <><ChevronRight size={11} /><span className="rounded-full bg-sky-600 px-1.5 py-0.5 text-[9px] font-bold text-white">3</span></>
                          }
                        </span>
                      </th>

                      {/* Categorías — clickable toggle */}
                      <th
                        colSpan={catColspan}
                        onClick={() => setCatExpanded(v => !v)}
                        className="cursor-pointer select-none border-b border-b-indigo-200 bg-indigo-50 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-indigo-600 transition-colors hover:bg-indigo-100"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <Layers size={11} />
                          Categorías de Producto
                          {catExpanded
                            ? <ChevronDown size={11} />
                            : <>
                                <ChevronRight size={11} />
                                <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold text-white">{catCols.length}</span>
                              </>
                          }
                        </span>
                      </th>
                    </tr>

                    {/* Fila 2: columnas individuales */}
                    <tr className="border-b-2 border-slate-200">
                      {/* Rendimiento expandido o colapsado */}
                      {rendExpanded ? (
                        <>
                          <th title="Presupuesto" className="border-r border-slate-100 bg-morado/5 px-3 py-2 text-right text-[10px] font-semibold text-slate-500">Ppto.</th>
                          <th className="border-r border-slate-100 bg-morado/5 px-3 py-2 text-right text-[10px] font-semibold text-slate-500">Venta</th>
                          <th
                            title="Porcentaje de Cumplimiento — clic para ordenar"
                            onClick={() => toggleOrden('PorcentajeCumplimiento')}
                            className="cursor-pointer select-none border-r border-slate-100 bg-morado/5 px-3 py-2 text-center text-[10px] font-semibold text-slate-500 transition-colors hover:bg-morado/10"
                          >
                            <span className="flex items-center justify-center gap-1">% Cumpl. <IconoOrden columna="PorcentajeCumplimiento" /></span>
                          </th>
                        </>
                      ) : (
                        <th className="border-r border-slate-100 bg-morado/5 px-3 py-2 text-center text-[10px] font-semibold text-morado/50 italic">clic para expandir</th>
                      )}

                      {/* Forma de pago expandida o colapsada */}
                      {pagoExpanded ? (
                        <>
                          <th className="border-r border-slate-100 bg-emerald-50/60 px-3 py-2 text-right text-[10px] font-semibold text-emerald-700">CrediPoint</th>
                          <th className="border-r border-slate-100 bg-emerald-50/60 px-3 py-2 text-right text-[10px] font-semibold text-emerald-700">Tarjeta</th>
                          <th className="border-r border-slate-100 bg-emerald-50/60 px-3 py-2 text-right text-[10px] font-semibold text-emerald-700">Contado</th>
                        </>
                      ) : (
                        <th className="border-r border-slate-100 bg-emerald-50/60 px-3 py-2 text-center text-[10px] font-semibold text-emerald-700/50 italic">clic para expandir</th>
                      )}

                      {/* Crédito expandido o colapsado */}
                      {creditoExpanded ? (
                        <>
                          <th title="Cédulas Ingresadas" className="border-r border-slate-100 bg-amber-50/60 px-3 py-2 text-right text-[10px] font-semibold text-amber-700">Céd.</th>
                          <th title="Pre-Aprobados" className="border-r border-slate-100 bg-amber-50/60 px-3 py-2 text-right text-[10px] font-semibold text-amber-700">Pre-Ap.</th>
                          <th title="No Aplica" className="border-r border-slate-100 bg-amber-50/60 px-3 py-2 text-right text-[10px] font-semibold text-red-500">N/A</th>
                          <th title="Facturados" className="border-r border-slate-100 bg-amber-50/60 px-3 py-2 text-right text-[10px] font-semibold text-emerald-600">Fact.</th>
                        </>
                      ) : (
                        <th className="border-r border-slate-100 bg-amber-50/60 px-3 py-2 text-center text-[10px] font-semibold text-amber-700/50 italic">clic para expandir</th>
                      )}

                      {/* Horario expandido o colapsado */}
                      {horarioExpanded ? (
                        <>
                          <th className="border-r border-slate-100 bg-sky-50/60 px-3 py-2 text-right text-[10px] font-semibold text-sky-700">Local</th>
                          <th title="Puerteo" className="border-r border-slate-100 bg-sky-50/60 px-3 py-2 text-right text-[10px] font-semibold text-sky-700">Puert.</th>
                          <th className="border-r border-slate-100 bg-sky-50/60 px-3 py-2 text-right text-[10px] font-semibold text-sky-700">Libre</th>
                        </>
                      ) : (
                        <th className="border-r border-slate-100 bg-sky-50/60 px-3 py-2 text-center text-[10px] font-semibold text-sky-700/50 italic">clic para expandir</th>
                      )}

                      {/* Categorías expandidas o colapsadas */}
                      {catExpanded ? (
                        <>
                          {dynamicCols.map(col => (
                            <th
                              key={col}
                              title={`${col} — clic para ordenar`}
                              onClick={() => toggleOrden(col)}
                              className="cursor-pointer select-none border-r border-slate-100 bg-indigo-50/60 px-2 py-2 text-right text-[10px] font-semibold text-indigo-600 whitespace-nowrap max-w-[80px] truncate transition-colors hover:bg-indigo-100"
                            >
                              <span className="inline-flex items-center gap-1">{col} <IconoOrden columna={col} /></span>
                            </th>
                          ))}
                          <th
                            title="Otros — clic para ordenar"
                            onClick={() => toggleOrden('Otros')}
                            className="cursor-pointer select-none bg-indigo-50/60 px-3 py-2 text-right text-[10px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
                          >
                            <span className="inline-flex items-center gap-1">Otros <IconoOrden columna="Otros" /></span>
                          </th>
                        </>
                      ) : (
                        <th className="bg-indigo-50/60 px-3 py-2 text-center text-[10px] font-semibold text-indigo-400 italic">
                          clic para expandir
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.map((entrada) => {
                      if (entrada.tipo === 'grupo') {
                        const { grupo, nivel } = entrada;
                        const clave = `${periodo}:${grupo.clave}`;
                        const expandido = !!gruposExpandidos[clave];
                        const cumplimientoGrupo = grupo.presupuesto > 0 ? (grupo.venta / grupo.presupuesto) * 100 : 0;
                        const esSupervisorGrupo = grupo.tipo === 'supervisor';
                        return (
                          <tr key={clave} className={esSupervisorGrupo ? 'border-b border-indigo-100 bg-indigo-50' : 'border-b border-slate-200 bg-slate-50'}>
                            <td colSpan={2} className={`sticky left-0 z-10 border-r border-slate-200 px-3 py-2 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] ${esSupervisorGrupo ? 'bg-indigo-50' : 'bg-slate-50'}`}>
                              <button
                                type="button"
                                onClick={() => toggleGrupo(clave)}
                                aria-expanded={expandido}
                                aria-label={`${expandido ? 'Contraer' : 'Expandir'} ${esSupervisorGrupo ? 'supervisor' : 'bodega'} ${grupo.nombre}`}
                                className={`flex min-h-11 w-full min-w-[190px] items-center gap-2 rounded-md py-1 text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-morado ${nivel === 1 && mostrarSupervisores ? 'pl-4' : ''} ${esSupervisorGrupo ? 'text-morado' : 'text-slate-700'}`}
                              >
                                {expandido ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                {esSupervisorGrupo ? <UserCheck size={14} /> : <Store size={14} />}
                                <span className="truncate" title={grupo.nombre}>{grupo.nombre}</span>
                              </button>
                            </td>
                            <td colSpan={columnasTabla - 2} className="px-4 py-2">
                              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 whitespace-nowrap text-[11px] text-slate-600">
                                {esSupervisorGrupo && <span>{grupo.bodegas.length} bodegas</span>}
                                <span>{grupo.filas.length} vendedores</span>
                                <span>Presupuesto: <strong className="text-slate-800">{fmtCurrency(grupo.presupuesto)}</strong></span>
                                <span>Venta: <strong className="text-slate-800">{fmtCurrency(grupo.venta)}</strong></span>
                                <span>Cumplimiento: <strong className="text-slate-800">{fmtPct(cumplimientoGrupo)}</strong></span>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      const row = entrada.fila;
                      const cumpl = parseFloat(row.PorcentajeCumplimiento || 0);
                      const rc = getCumplColor(cumpl);
                      const barW = Math.min(Math.max(cumpl, 0), 100);
                      const rowNum = entrada.indice + 1;

                      return (
                        <tr
                          key={`${row.idSupervisor}-${row.idBodega}-${row.idPersonal}-${row.Periodo}-${entrada.indice}`}
                          className="group border-b border-slate-100 transition-colors hover:bg-slate-50/70"
                        >
                          {/* # */}
                          <td className="border-r border-slate-100 px-3 py-2.5 text-center text-[10px] font-medium text-slate-400">
                            {rowNum}
                          </td>

                          {/* Vendedor — columna fija (sticky) al hacer scroll horizontal */}
                          <td className={`sticky left-0 z-10 border-r border-slate-100 bg-white px-3 py-2.5 max-w-[200px] shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] group-hover:bg-slate-50 ${mostrarJerarquia ? 'pl-6' : ''}`}>
                            <div className="flex items-center gap-1.5">
                              <span className="truncate font-semibold text-slate-800" title={row.NombreCompleto}>{row.NombreCompleto}</span>
                              {rol === 'vendedor' && (
                                <button
                                  onClick={() => setModalVendedor({ idPersonal: row.idPersonal, NombreCompleto: row.NombreCompleto })}
                                  title="Abrir gestor de oportunidades"
                                  className="flex-shrink-0 rounded-md p-1 text-morado/60 hover:bg-morado/10 hover:text-morado transition-colors"
                                >
                                  <UserCheck size={13} />
                                </button>
                              )}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              <span title="Tiempo en empresa" className="inline-flex items-center gap-1 rounded-md bg-morado/8 px-1.5 py-0.5">
                                <Briefcase size={9} className="text-morado/60" />
                                <span className="text-[9px] font-medium text-morado/80">{row.TiempoEmpresaAnios}a {row.TiempoEmpresaMeses}m</span>
                              </span>
                              <span title="Edad" className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5">
                                <User size={9} className="text-slate-400" />
                                <span className="text-[9px] font-medium text-slate-600">{row.EdadAnios} años</span>
                              </span>
                              {parseFloat(row.Sueldo) > 0 && (
                                <span title="Sueldo" className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5">
                                  <DollarSign size={9} className="text-emerald-500" />
                                  <span className="text-[9px] font-medium text-emerald-700">{fmtCurrency(row.Sueldo)}</span>
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Rendimiento — expandido o colapsado */}
                          {rendExpanded ? (
                            <>
                              <td className="border-r border-slate-100 px-3 py-2.5 text-right font-medium text-slate-600">
                                {fmtCurrency(row.Presupuesto) ?? <EmptyCell />}
                              </td>
                              <td className="border-r border-slate-100 px-3 py-2.5 text-right font-bold text-slate-800">
                                {fmtCurrency(row.Venta) ?? <EmptyCell />}
                              </td>
                              <td className="border-r border-slate-100 px-3 py-2.5">
                                <div className="flex min-w-[64px] flex-col items-center gap-1">
                                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ring-1 ${rc.bg} ${rc.text} ${rc.ring}`}>
                                    {fmtPct(cumpl)}
                                  </span>
                                  <div className="w-full rounded-full bg-slate-200 h-[3px]">
                                    <div
                                      className={`${rc.bar} h-[3px] rounded-full transition-all`}
                                      style={{ width: `${barW}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                            </>
                          ) : (
                            <td className="border-r border-slate-100 bg-morado/5 px-3 py-2.5 text-center">
                              <button onClick={() => setRendExpanded(true)} className="text-[10px] font-medium text-morado/50 hover:text-morado transition-colors">ver</button>
                            </td>
                          )}

                          {/* Forma de pago — expandida o colapsada */}
                          {pagoExpanded ? (
                            <>
                              <td className="border-r border-slate-100 bg-emerald-50/10 px-3 py-2.5 text-right text-slate-700">
                                {renderDynCell(row.CREDIPOINT)}
                              </td>
                              <td className="border-r border-slate-100 bg-emerald-50/10 px-3 py-2.5 text-right text-slate-700">
                                {renderDynCell(row.TARJETA)}
                              </td>
                              <td className="border-r border-slate-100 bg-emerald-50/10 px-3 py-2.5 text-right text-slate-700">
                                {renderDynCell(row.CONTADO)}
                              </td>
                            </>
                          ) : (
                            <td className="border-r border-slate-100 bg-emerald-50/10 px-3 py-2.5 text-center">
                              <button onClick={() => setPagoExpanded(true)} className="text-[10px] font-medium text-emerald-700/50 hover:text-emerald-700 transition-colors">ver</button>
                            </td>
                          )}

                          {/* Crédito — expandido o colapsado */}
                          {creditoExpanded ? (
                            <>
                              <td className="border-r border-slate-100 bg-amber-50/10 px-3 py-2.5 text-right font-medium text-slate-700">
                                {parseInt(row.CedulasIngresadas) || 0}
                              </td>
                              <td className="border-r border-slate-100 bg-amber-50/10 px-3 py-2.5 text-right font-medium text-amber-700">
                                {parseInt(row.preAprobado) || 0}
                              </td>
                              <td className="border-r border-slate-100 bg-amber-50/10 px-3 py-2.5 text-right font-medium text-red-500">
                                {parseInt(row.noAplica) || 0}
                              </td>
                              <td className="border-r border-slate-100 bg-amber-50/10 px-3 py-2.5 text-right font-bold text-emerald-600">
                                {parseInt(row.facturados) || 0}
                              </td>
                            </>
                          ) : (
                            <td className="border-r border-slate-100 bg-amber-50/10 px-3 py-2.5 text-center">
                              <button onClick={() => setCreditoExpanded(true)} className="text-[10px] font-medium text-amber-700/50 hover:text-amber-700 transition-colors">ver</button>
                            </td>
                          )}

                          {/* Horario — expandido o colapsado */}
                          {horarioExpanded ? (
                            <>
                              <td className="border-r border-slate-100 bg-sky-50/10 px-3 py-2.5 text-right text-slate-700">
                                {parseInt(row.DiasLocal) || 0}
                              </td>
                              <td className="border-r border-slate-100 bg-sky-50/10 px-3 py-2.5 text-right text-slate-700">
                                {parseInt(row.DiasPuerteo) || 0}
                              </td>
                              <td className="border-r border-slate-100 bg-sky-50/10 px-3 py-2.5 text-right text-slate-700">
                                {parseInt(row.DiasLibre) || 0}
                              </td>
                            </>
                          ) : (
                            <td className="border-r border-slate-100 bg-sky-50/10 px-3 py-2.5 text-center">
                              <button onClick={() => setHorarioExpanded(true)} className="text-[10px] font-medium text-sky-700/50 hover:text-sky-700 transition-colors">ver</button>
                            </td>
                          )}

                          {/* Categorías — al final */}
                          {catExpanded ? (
                            <>
                              {dynamicCols.map(col => (
                                <td key={col} className="border-r border-slate-100 bg-indigo-50/10 px-2 py-2.5 text-right text-slate-700">
                                  {renderDynCell(row[col])}
                                </td>
                              ))}
                              <td className="bg-indigo-50/10 px-3 py-2.5 text-right text-slate-700">
                                {renderDynCell(row.Otros)}
                              </td>
                            </>
                          ) : (
                            <td className="bg-indigo-50/10 px-3 py-2.5 text-center">
                              <button
                                onClick={() => setCatExpanded(true)}
                                className="text-[10px] font-medium text-indigo-400 hover:text-indigo-600 transition-colors"
                              >
                                ver {catCols.length} categorías
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Fila de totales del período */}
                    <tr className="border-t-2 border-morado/20 bg-morado/5">
                      <td colSpan={2} className="sticky left-0 z-10 border-r border-slate-200 bg-morado/5 px-3 py-3 text-[10px] font-bold uppercase tracking-widest text-morado shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]">
                        Total período
                      </td>
                      {rendExpanded ? (
                        <>
                          <td className="border-r border-slate-200 px-3 py-3 text-right text-xs font-bold text-slate-700">
                            {fmtCurrency(totalPresupuesto)}
                          </td>
                          <td className="border-r border-slate-200 px-3 py-3 text-right text-xs font-bold text-slate-800">
                            {fmtCurrency(totalVenta)}
                          </td>
                          <td className="border-r border-slate-200 px-3 py-3">
                            <div className="flex flex-col items-center gap-1 min-w-[64px]">
                              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ring-1 ${cc.bg} ${cc.text} ${cc.ring}`}>
                                {fmtPct(avgCumpl)}
                              </span>
                            </div>
                          </td>
                        </>
                      ) : (
                        <td className="border-r border-slate-200 px-3 py-3" />
                      )}

                      {/* Forma de pago — solo cuando es monto */}
                      {pagoExpanded ? (
                        <>
                          <td className="border-r border-slate-200 bg-emerald-50/10 px-3 py-3 text-right text-xs font-bold text-emerald-700">
                            {mostrarPorcentaje ? '' : fmtCurrency(totalCredi)}
                          </td>
                          <td className="border-r border-slate-200 bg-emerald-50/10 px-3 py-3 text-right text-xs font-bold text-emerald-700">
                            {mostrarPorcentaje ? '' : fmtCurrency(totalTarjeta)}
                          </td>
                          <td className="border-r border-slate-200 bg-emerald-50/10 px-3 py-3 text-right text-xs font-bold text-emerald-700">
                            {mostrarPorcentaje ? '' : fmtCurrency(totalContado)}
                          </td>
                        </>
                      ) : (
                        <td className="border-r border-slate-200 bg-emerald-50/10 px-3 py-3" />
                      )}

                      {/* Crédito */}
                      {creditoExpanded ? (
                        <>
                          <td className="border-r border-slate-200 bg-amber-50/10 px-3 py-3 text-right text-xs font-bold text-slate-700">
                            {totalCedulas}
                          </td>
                          <td className="border-r border-slate-200 bg-amber-50/10 px-3 py-3 text-right text-xs font-bold text-emerald-600">
                            {totalFacturados}
                          </td>
                          <td className="border-r border-slate-200 bg-amber-50/10 px-3 py-3 text-right text-xs font-bold text-red-500">
                            {totalNoAplica}
                          </td>
                          <td className="border-r border-slate-200 bg-amber-50/10 px-3 py-3 text-right text-xs font-bold text-amber-700">
                            {totalPreAprob}
                          </td>
                        </>
                      ) : (
                        <td className="border-r border-slate-200 bg-amber-50/10 px-3 py-3" />
                      )}

                      {/* Horario — no aplica sumar */}
                      {horarioExpanded ? (
                        <>
                          <td className="border-r border-slate-200 bg-sky-50/10 px-3 py-3" />
                          <td className="border-r border-slate-200 bg-sky-50/10 px-3 py-3" />
                          <td className="border-r border-slate-200 bg-sky-50/10 px-3 py-3" />
                        </>
                      ) : (
                        <td className="border-r border-slate-200 bg-sky-50/10 px-3 py-3" />
                      )}

                      {/* Categorías vacías en totales — al final */}
                      {Array.from({ length: catColspan }).map((_, i) => (
                        <td key={i} className="bg-indigo-50/10 px-2 py-3" />
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ── Paginación ─────────────────────────────────────── */}
              {totalItems > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm">

                  {/* Info */}
                  <span className="text-xs text-slate-500">
                    Mostrando <span className="font-semibold text-slate-700">{firstItem}–{lastItem}</span> de{' '}
                    <span className="font-semibold text-slate-700">{totalItems}</span> {mostrarJerarquia ? tipoGrupo : rolMeta.personaPlural}
                    {mostrarJerarquia && <span> · {rows.length} vendedores</span>}
                  </span>

                  {/* Navegación */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(periodo, page - 1)}
                      disabled={page === 1}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft size={13} /> Anterior
                    </button>

                    {/* Páginas numéricas — máx 5 visibles */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => {
                        if (totalPages <= 5) return true;
                        if (p === 1 || p === totalPages) return true;
                        return Math.abs(p - page) <= 1;
                      })
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '…' ? (
                          <span key={`ellipsis-${i}`} className="px-1 text-xs text-slate-400">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setPage(periodo, p)}
                            className={`min-w-[28px] rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${
                              p === page
                                ? 'border-morado bg-morado text-white shadow-sm'
                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )
                    }

                    <button
                      onClick={() => setPage(periodo, page + 1)}
                      disabled={page === totalPages}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Siguiente <ChevronRight size={13} />
                    </button>
                  </div>

                  {/* Filas por página */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{mostrarJerarquia ? 'Grupos:' : 'Filas:'}</span>
                    {[10, 15, 25, 50].map(n => (
                      <button
                        key={n}
                        onClick={() => setRowsPerPage(n)}
                        className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors ${
                          rowsPerPage === n
                            ? 'border-morado bg-morado text-white'
                            : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>

                </div>
              )}
              </>
              )}

            </div>
          );
        })}

      </div>

      {modalVendedor && (
        <ClientesAprobadosModal
          idVendedor={modalVendedor.idPersonal}
          nombreVendedor={modalVendedor.NombreCompleto}
          onClose={() => setModalVendedor(null)}
        />
      )}
    </div>
  );
}
